import React from 'react';
import { bool, func, number, object, string } from 'prop-types';
import classNames from 'classnames';
import { Field, Form as FinalForm } from 'react-final-form';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';

import { Form, RangeSlider } from '../../../components';

import css from './PriceFilterForm.module.css';

// Keep temporary input values editable; enforce range limits on blur and submit.
const parsePrice = value => {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isNaN(parsedValue) ? '' : parsedValue;
};

const normalizeRange = (values, min, max) => {
  const clamp = (value, fallback) =>
    typeof value === 'number' && Number.isFinite(value)
      ? Math.min(max, Math.max(min, value))
      : fallback;
  const minPrice = clamp(values.minPrice, min);
  const maxPrice = Math.max(minPrice, clamp(values.maxPrice, max));
  return { minPrice, maxPrice };
};

const PriceInput = ({ input, meta, onPriceBlur, placeholder, ...rest }) => {
  const previousValue = React.useRef();

  return (
    <input
      {...rest}
      {...input}
      placeholder={meta.active ? '' : placeholder}
      onFocus={event => {
        previousValue.current = input.value;
        input.onFocus(event);
        input.onChange('');
      }}
      onBlur={event => {
        if (event.target.value === '' && previousValue.current !== undefined) {
          input.onChange(previousValue.current);
        }
        previousValue.current = undefined;
        onPriceBlur(input.name);
        input.onBlur(event);
      }}
    />
  );
};

// PriceFilterForm component
const PriceFilterFormComponent = props => {
  const { liveEdit, onSubmit, onCancel, onClear, ...rest } = props;

  if (liveEdit && !onSubmit) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onSubmit function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    return onSubmit({
      ...normalizeRange({ minPrice, maxPrice }, rest.min, rest.max),
      ...restValues,
    });
  };

  const formCallbacks = liveEdit
    ? { onSubmit: handleSubmit }
    : { onSubmit: handleSubmit, onCancel, onClear };
  return (
    <FinalForm
      {...rest}
      {...formCallbacks}
      render={formRenderProps => {
        const {
          form,
          handleSubmit,
          id,
          showAsPopup,
          onClear,
          onCancel,
          isOpen,
          isInSideBar,
          contentRef,
          style,
          intl,
          values,
          min,
          max,
          step,
          children,
        } = formRenderProps;
        const { minPrice, maxPrice } = normalizeRange(values, min, max);

        const handlePriceBlur = name => {
          const currentValues = form.getState().values;
          const start = normalizeRange({ minPrice: currentValues.minPrice }, min, max).minPrice;
          const end = normalizeRange({ maxPrice: currentValues.maxPrice }, min, max).maxPrice;
          form.change(name, name === 'minPrice' ? Math.min(start, end) : Math.max(start, end));
        };

        const handleCancel = () => {
          // reset the final form to initialValues
          form.reset();
          onCancel();
        };

        const clear = intl.formatMessage({ id: 'PriceFilterForm.clear' });
        const cancel = intl.formatMessage({ id: 'PriceFilterForm.cancel' });
        const submit = intl.formatMessage({ id: 'PriceFilterForm.submit' });
        const ok = intl.formatMessage({ id: 'PriceFilterForm.ok', defaultMessage: 'OK' });

        const classes = classNames(css.root, {
          [css.popup]: showAsPopup,
          [css.isOpenAsPopup]: showAsPopup && isOpen,
          [css.plain]: !showAsPopup,
          [css.isOpen]: !showAsPopup && isOpen,
        });

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            tabIndex="0"
            contentRef={contentRef}
            style={{ minWidth: '300px', ...style }}
          >
            <div className={css.contentWrapper}>
              {showAsPopup ? (
                <span className={css.label}>
                  <FormattedMessage id="PriceFilterForm.label" />
                </span>
              ) : null}
              <div className={css.inputsWrapper}>
                <Field
                  className={classNames(css.minPrice, { [css.priceInSidebar]: isInSideBar })}
                  id={`${id}.minPrice`}
                  name="minPrice"
                  component={PriceInput}
                  onPriceBlur={handlePriceBlur}
                  type="number"
                  placeholder={min}
                  min={min}
                  max={max}
                  step={step}
                  parse={parsePrice}
                />
                <span className={css.priceSeparator}>-</span>
                <Field
                  className={classNames(css.maxPrice, { [css.priceInSidebar]: isInSideBar })}
                  id={`${id}.maxPrice`}
                  name="maxPrice"
                  component={PriceInput}
                  onPriceBlur={handlePriceBlur}
                  type="number"
                  placeholder={max}
                  min={min}
                  max={max}
                  step={step}
                  parse={parsePrice}
                />
                {liveEdit ? (
                  <button className={css.okButton} type="submit">
                    {ok}
                  </button>
                ) : null}
              </div>
            </div>

            <div className={css.sliderWrapper}>
              <RangeSlider
                min={min}
                max={max}
                step={step}
                handles={[minPrice, maxPrice]}
                onChange={handles => {
                  form.change('minPrice', handles[0]);
                  form.change('maxPrice', handles[1]);
                }}
              />
            </div>

            {!liveEdit ? (
              <div className={css.buttonsWrapper}>
                <button className={css.clearButton} type="button" onClick={onClear}>
                  {clear}
                </button>
                <button className={css.cancelButton} type="button" onClick={handleCancel}>
                  {cancel}
                </button>
                <button className={css.submitButton} type="submit">
                  {submit}
                </button>
              </div>
            ) : null}
            {children}
          </Form>
        );
      }}
    />
  );
};

PriceFilterFormComponent.defaultProps = {
  liveEdit: false,
  showAsPopup: false,
  isOpen: false,
  contentRef: null,
  style: null,
  min: 0,
  step: 1,
  onCancel: null,
  onClear: null,
  onSubmit: null,
};

PriceFilterFormComponent.propTypes = {
  id: string.isRequired,
  liveEdit: bool,
  showAsPopup: bool,
  onCancel: func,
  onClear: func,
  onSubmit: func,
  isOpen: bool,
  contentRef: func,
  style: object,
  min: number.isRequired,
  max: number.isRequired,
  step: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

const PriceFilterForm = injectIntl(PriceFilterFormComponent);

export default PriceFilterForm;
